import { useEffect, useRef, useState } from 'react';
import { 
  LoginAsync, 
  OTPCompleted, 
  SelectCurrentUser, 
  SelectErrorMessage, 
  SelectIsLoading, 
  SelectIsReadyForOTP, 
  selectLoginModalShow, 
  setErrorMessage, 
  setIsReadyForOTP, 
  setLoginModalShow 
} from '../features/authentication/authenticationSlice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { Box, Button, Modal, Stack, TextField } from '@mui/material';
import { loginModalStyle } from '../app/consts';
import { toast } from 'react-toastify';

const Authentication = () => {
  const dispatch = useAppDispatch();
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');

  const loginModalShow = useAppSelector(selectLoginModalShow);
  const isLoading = useAppSelector(SelectIsLoading);
  const errorMsg = useAppSelector(SelectErrorMessage);
  const currentUser = useAppSelector(SelectCurrentUser);
  const isReadyForOTP = useAppSelector(SelectIsReadyForOTP);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const codeRef = useRef<number>(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const [displaySeconds, setDisplaySeconds] = useState(45);
  const [userInput, setUserInput] = useState<string[]>(new Array(6).fill(''));
  const [resendAvail, setResendAvail] = useState(false);

  // Clear timer when modal is closed or component unmounts
  const clearTimer = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const handleClose = () => {
    clearTimer();
    dispatch(setLoginModalShow(false));
    dispatch(setIsReadyForOTP(false));
    setUserInput(new Array(6).fill(''));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const value = e.target.value;
    if (value && isNaN(Number(value))) return;

    const temp = [...userInput];
    temp[index] = value.slice(-1);
    setUserInput(temp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
      inputRefs.current[index + 1]?.select();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Backspace' && !userInput[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      inputRefs.current[index - 1]?.select();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(paste)) return;

    const digits = paste.slice(0, 6).split('');
    const newOtp = Array(6).fill('');
    digits.forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setUserInput(newOtp);

    const lastIndex = Math.min(digits.length, 6) - 1;
    inputRefs.current[lastIndex]?.focus();
    inputRefs.current[lastIndex]?.select();
  };

  const startCountdown = () => {
    clearTimer();
    setResendAvail(false);
    setDisplaySeconds(45);
    const dest = Date.now() + 45000;

    countdownIntervalRef.current = setInterval(() => {
      const distance = dest - Date.now();
      const secondsLeft = Math.max(0, Math.floor(distance / 1000));
      setDisplaySeconds(secondsLeft);

      if (distance <= 0) {
        clearTimer();
        setResendAvail(true);
      }
    }, 1000);
  };

  const sendOTPMessage = (email: string, name: string) => {
    const code = Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000;
    codeRef.current = code;

    // Trigger your OTP service here with `codeRef.current` and user email/name
    console.log(`[DEV OTP] Generated code for ${name} (${email}): ${code}`);
    startCountdown();
  };

  const handleSubmit = () => {
    if (!userName.trim() || !password.trim()) {
      dispatch(setErrorMessage("קיים חוסר בפרטים"));
      return;
    }

    dispatch(LoginAsync({ username: userName, password: password }))
      .unwrap()
      .then((user) => {
        // user is the payload returned directly from /login/ view: { name, email, isSU, mfa }
        setUserName("");
        setPassword("");
        if (!user.mfa) {
          toast.success('התחברות בוצעה בהצלחה');
          dispatch(OTPCompleted());
          return;
        }

        if (!user.email) {
          dispatch(setErrorMessage("לא נמצאה כתובת דואר אלקטרוני עבור המשתמש"));
          return;
        }

        sendOTPMessage(user.email, user.name);
        dispatch(setIsReadyForOTP(true));
      })
      .catch((error) => {
        dispatch(setErrorMessage(typeof error === 'string' ? error : "שם משתמש / סיסמא שגויים"));
      });
  };

  const handleDone = () => {
    const userCodeInput = userInput.join("");
    if (codeRef.current?.toString() === userCodeInput) {
      clearTimer();
      dispatch(setIsReadyForOTP(false));
      toast.success('התחברות בוצעה בהצלחה');
      dispatch(OTPCompleted());
      setUserInput(new Array(6).fill(''));
    } else {
      toast.error("הקוד לא תואם, נסה שנית");
    }
  };

  return (
    <Modal open={loginModalShow} onClose={handleClose}>
      <Box sx={loginModalStyle}>
        <p style={{ fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }} onClick={handleClose}>X</p>
        
        {isLoading ? (
          <img src='/FadingCircles.gif' alt='spinner' />
        ) : isReadyForOTP ? (
          <div>
            קוד חד פעמי נשלח לכתובת הדואר האלקטרוני שהזנת במעמד ההרשמה.<br />
            אנא הזן את הקוד ולחץ על הכפתור המשך<br /><br /><br /><br />
            <Stack direction="row-reverse" sx={{ justifyContent: "center", alignItems: "center" }}>
              {userInput.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  slotProps={{
                    htmlInput: {
                      maxLength: 1,
                      style: { textAlign: 'center' },
                      inputMode: 'numeric',
                      autoComplete: 'one-time-code',
                    }
                  }}
                  variant="standard"
                  sx={{ marginLeft: 2 }}
                />
              ))}
            </Stack><br /><br /><br />
            
            {resendAvail ? (
              <Button onClick={() => {
                if (currentUser?.email) {
                  sendOTPMessage(currentUser.email, currentUser.name);
                }
              }}>
                שלח קוד חדש
              </Button>
            ) : (
              <p>אנא המתינו {displaySeconds} שניות לשליחת קוד חדש</p>
            )}
            <br />
            <Button id='submit' type='button' onClick={handleDone} variant="contained" size='large' color='info'>
              המשך
            </Button>
          </div>
        ) : (
          <form 
            id='login_form' 
            style={{ textAlign: 'center', fontSize: '30px' }} 
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
          >
            <p>אנא התחבר</p>
            <TextField 
              variant="filled" 
              label='שם משתמש' 
              type='text' 
              name='username' 
              value={userName}
              placeholder='שם משתמש' 
              onChange={(e) => setUserName(e.target.value)} 
              required 
              style={{ fontSize: '20px', width: '80%', textAlign: 'center' }} 
            />
            <br /><br />
            <TextField 
              variant="filled" 
              label='סיסמא' 
              type='password' 
              name='pass' 
              value={password}
              placeholder='סיסמא' 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              style={{ fontSize: '20px', width: '80%', textAlign: 'center' }} 
            />
            <br /><br />
            <Button type='submit' variant="contained" size='large' color='info'>
              התחבר
            </Button>
            {errorMsg && <p style={{ color: 'red', fontSize: '16px', marginTop: '10px' }}>{errorMsg}</p>}
          </form>
        )}
      </Box>
    </Modal>
  );
};

export default Authentication;